import React from "react";
import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import { formatWeight } from "@helpers/formatWeight";
import { useIsTablet } from "@hooks/use-is-tablet";
import { useSidebar } from "@context/sidebar-context";

const formatNumber = (v: number | string | null | undefined) => {
  if (v === null || v === undefined) return "0";

  const num = Number(v);
  if (Number.isNaN(num)) return "0";
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2);
};

export const Footer = ({ meta }: { meta: Record<string, number> | null }) => {
  const isTablet = useIsTablet();
  const { isOpen: isSidebarOpen } = useSidebar();
  const shouldStack = isTablet && isSidebarOpen;
  const useCompactLayout = isTablet;

  let gridTemplateColumns: string | undefined;
  if (useCompactLayout) {
    gridTemplateColumns = shouldStack ? "1fr" : "repeat(2, minmax(0, 1fr))";
  }

  let paginationAlignSelf: "center" | "stretch" | "auto" = "auto";
  if (useCompactLayout) {
    paginationAlignSelf = shouldStack ? "center" : "stretch";
  }

  return (
    <GridFooterContainer
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: useCompactLayout ? "column" : "row",
        alignItems: useCompactLayout ? "stretch" : "center",
        gap: useCompactLayout ? 0.75 : 0,
        px: useCompactLayout ? 1 : 2,
        py: useCompactLayout ? 0.75 : 1,
        minHeight: useCompactLayout ? "unset" : "52px",
        height: "auto !important",
      }}
    >
      <Box
        sx={{
          display: useCompactLayout ? "grid" : "flex",
          gridTemplateColumns,
          columnGap: useCompactLayout ? 1 : undefined,
          rowGap: useCompactLayout ? 0.5 : undefined,
          flexDirection: shouldStack ? "column" : "row",
          alignItems: "flex-start",
          flex: 1,
          minWidth: 0,
          flexWrap: useCompactLayout ? "nowrap" : "wrap",
          gap: useCompactLayout ? 0 : 1,
        }}
      >
        {meta && (
          <>
            <FooterItem
              label="Total Pieces:"
              value={formatNumber(meta.total_pieces)}
              isTablet={isTablet}
              shouldStack={shouldStack}
            />
            <FooterItem
              label="Total Net Weight:"
              value={formatWeight(meta.total_net_weight)}
              isTablet={isTablet}
              shouldStack={shouldStack}
            />
            <FooterItem
              label="Total Gross Weight:"
              value={formatWeight(meta.total_gross_weight)}
              isTablet={isTablet}
              shouldStack={shouldStack}
            />
            <FooterItem
              label="Total Invoice Amount:"
              value={formatNumber(meta.total_invoice_amount)}
              isTablet={isTablet}
              shouldStack={shouldStack}
            />
          </>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          alignSelf: paginationAlignSelf,
          width: useCompactLayout ? "100%" : "auto",
          "& .MuiTablePagination-toolbar": {
            minHeight: useCompactLayout ? 34 : 52,
            px: useCompactLayout ? 0 : 2,
            justifyContent: useCompactLayout ? "flex-end" : "inherit",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: useCompactLayout ? "0.8rem" : "0.875rem",
              m: useCompactLayout ? "0 6px" : undefined,
            },
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            p: useCompactLayout ? 0.5 : 1,
          },
        }}
      >
        <GridPagination />
      </Box>
    </GridFooterContainer>
  );
};

const FooterItem = ({
  label,
  value,
  isTablet,
  shouldStack,
}: {
  label: string;
  value: any;
  isTablet: boolean;
  shouldStack: boolean;
}) => {
  let flexDirection: "row" | "column" = "row";
  let gap = 1;
  let alignItems: "center" | "flex-start" = "center";
  let pr = 3;
  let mr = 3;

  if (shouldStack) {
    flexDirection = "row";
    gap = 0.5;
    alignItems = "center";
    pr = 0;
    mr = 0;
  } else if (isTablet) {
    flexDirection = "row";
    gap = 0.5;
    alignItems = "center";
    pr = 0;
    mr = 0;
  } else {
    flexDirection = "column";
    gap = 0;
    alignItems = "flex-start";
    pr = 2;
    mr = 1;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection,
        gap,
        alignItems,
        pr,
        mr,
        flexShrink: 1,
        minWidth: 0,
        transition: "all 0.3s ease",
      }}
    >
      <Typography
        fontSize={isTablet ? "12px" : "15px"}
        sx={{
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        fontSize={isTablet ? "13px" : "15px"}
        sx={{
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};
